
import { supabase } from "@/integrations/supabase/client";
import { PostgrestError, PostgrestSingleResponse } from "@supabase/supabase-js";

/**
 * Type representing any table name in the database
 * Must match exactly with the tables that exist in the Supabase database
 */
export type TableName = 
  | 'profiles'
  | 'groups'
  | 'group_members'
  | 'expenses'
  | 'expense_shares'
  | 'settlements'
  | 'payment_methods'
  | 'exchange_rates'
  | 'users';

/**
 * Safely extracts data from a Supabase query result
 * @param result The result object from a Supabase query
 * @returns An object with data, error, and success properties
 */
export const safeExtract = <T>(
  result: PostgrestSingleResponse<T> | { data: T | null; error: PostgrestError | null }
): { data: T | null; error: string | null; success: boolean } => {
  if (result.error) {
    console.error("Supabase query error:", result.error);
    return {
      data: null,
      error: result.error.message,
      success: false,
    };
  }
  
  return {
    data: result.data,
    error: null,
    success: true,
  };
};

/**
 * Safe way to select a single item from a table
 */
export const safeSelect = async <T>(
  table: TableName,
  filter: Record<string, any> = {},
  options: {
    select?: string;
    single?: boolean;
    order?: { column: string; ascending?: boolean };
  } = {}
): Promise<{ data: T | null; error: string | null; success: boolean }> => {
  try {
    // Use type assertion to avoid TypeScript errors with dynamic table names
    let baseQuery = supabase.from(table as any).select(options.select || "*");

    // Apply filters
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) {
        baseQuery = baseQuery.eq(key, value);
      }
    }
    
    let result;
    // Apply single query
    if (options.single) {
      result = await baseQuery.maybeSingle();
    } else {
      // Apply order
      if (options.order) {
        baseQuery = baseQuery.order(options.order.column, {
          ascending: options.order.ascending,
        });
      }
      
      result = await baseQuery.limit(1);
      if (Array.isArray(result.data) && result.data.length > 0) {
        return {
          data: result.data[0] as T,
          error: null,
          success: true
        };
      }
    }
    
    return {
      data: result.data as T | null,
      error: result.error ? result.error.message : null,
      success: !result.error
    };
  } catch (error: any) {
    console.error("Error in safeSelect:", error);
    return {
      data: null,
      error: error.message || "Unknown error",
      success: false,
    };
  }
};

/**
 * Safe way to select multiple items from a table
 */
export const safeSelectArray = async <T>(
  table: TableName,
  filter: Record<string, any> = {},
  options: {
    select?: string;
    limit?: number;
    order?: { column: string; ascending?: boolean };
  } = {}
): Promise<{ data: T[] | null; error: string | null; success: boolean }> => {
  try {
    // Use type assertion to avoid TypeScript errors with dynamic table names
    let baseQuery = supabase.from(table as any).select(options.select || "*");

    // Apply filters
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) {
        baseQuery = baseQuery.eq(key, value);
      }
    }

    // Apply order
    if (options.order) {
      baseQuery = baseQuery.order(options.order.column, {
        ascending: options.order.ascending,
      });
    }

    // Apply limit
    if (options.limit) {
      baseQuery = baseQuery.limit(options.limit);
    }
    
    const result = await baseQuery;
    
    return {
      data: result.data as T[] | null,
      error: result.error ? result.error.message : null,
      success: !result.error
    };
  } catch (error: any) {
    console.error("Error in safeSelectArray:", error);
    return {
      data: null,
      error: error.message || "Unknown error",
      success: false,
    };
  }
};

/**
 * Safe way to insert data into a table
 */
export const safeInsert = async <T>(
  table: TableName,
  data: any,
  options: {
    returning?: string;
  } = {}
): Promise<{ data: T | null; error: string | null; success: boolean }> => {
  try {
    // Use type assertion to avoid TypeScript errors with dynamic table names
    const result = await supabase
      .from(table as any)
      .insert(data)
      .select(options.returning || "*")
      .single();
      
    return {
      data: result.data as T | null,
      error: result.error ? result.error.message : null,
      success: !result.error
    };
  } catch (error: any) {
    console.error("Error in safeInsert:", error);
    return {
      data: null,
      error: error.message || "Unknown error",
      success: false,
    };
  }
};

/**
 * Safe cast from unknown to a specific type, with a fallback value
 */
export const safeCast = <T>(data: unknown, fallback: T): T => {
  return data as T || fallback;
};
